import * as _ from 'lodash';
import { NextRegentsAdminDate } from '../../../../../ng2/shared/constants/next-regents-admin-date.constant';
import { RegentsSubject } from '../../../../../ng2/shared/constants/regents.constant';
import { IRegentsExam } from '../../../../../ng2/shared/constants/regents.constant';
import { ISubjectArea } from '../../../../../ng2/shared/constants/subject-areas.constant';
import { ImStudent } from '../../../../../ng2/shared/services/im-models/im-student.service';
import {
  ICurrProgramCourse,
  ICurrProgramCourseMark,
} from '../../../../../ng2/shared/typings/interfaces/student.interface';
import { ICourseWarnings } from '../../../../shared/static-models/im-models/im-student-current-program';
import {
  ImStudentCurrentProgramHelpers,
  TValidGradeStatuses,
} from '../../../../shared/static-models/im-models/im-student-current-program-helpers';
import { ImStudentCurrentProgram } from './../../../../shared/static-models/im-models/im-student-current-program';
import { ImStudentRegents } from './../../../../shared/static-models/im-models/im-student-regents';

type TValidTrendStatus = 'IMPROVING' | 'DECLINING' | 'NO_CHANGE';

interface IMarkWithStatus extends ICurrProgramCourseMark {
  status: TValidGradeStatuses;
  display: string;
}

interface ICourseMarkTrend {
  previous: number;
  latest: number;
  status: TValidTrendStatus;
}

interface ITableRow {
  schedRegentsExamsForSubject: IRegentsExam[];
  cyclePeriod: string;
  period: string | number;
  subjectArea: ISubjectArea;
  courseCode: string;
  courseName: string;
  teacher: string;
  creditValue: number;
  grades: IMarkWithStatus[];
  trend: ICourseMarkTrend;
  status: TValidGradeStatuses;
  priority: string;
  prepsFor: string[];
  courseId: string;
  warningInfo: ICourseWarnings;
  nx: string;
}

export class StudentCurrentProgramPanelCtrl {
  static $inject = ['$scope', 'ImStudent', 'ImStudentRegents', 'ImStudentCurrentProgram'];

  public tableRows: ITableRow[];
  public title = 'Current Program';
  public nextRegentsAdminDate = NextRegentsAdminDate;
  public studentName: string;

  // Bindings
  private currentSchool;
  private student;

  // Default sort by period
  query = {
    order: 'period',
  };
  private courses: ICurrProgramCourse[];

  constructor(
    private $scope,
    private imStudent: ImStudent,
    private imStudentRegents: ImStudentRegents,
    private imStudentCurrentProgram: ImStudentCurrentProgram,
  ) {}

  $onInit() {
    // not in SorterColumn
    this.courses = this.imStudent.getCoursesForCurrentTermYear(this.student);
    this.studentName = this.imStudent.fullName(this.student);

    // TABLE DATA
    this.tableRows = this._mapTableRows();

    this.$scope.$watch(
      () => {
        return this.student.nextScheduledRegents;
      },
      () => {
        this._updateRegentsSchedCol();
      },
      true,
    );

    this.$scope.hover = function($event) {
      const tooltipEle = document.getElementsByClassName('nv-tooltip');
      if (tooltipEle.length === 1) {
        tooltipEle[0].setAttribute(
          'style',
          'position:fixed; top:' + ($event.y + 25) + 'px; left:' + ($event.x - 20) + 'px; max-width:250px;',
        );
      }
    };
  }

  _mapTableRows(): ITableRow[] {
    return _.map(_.toArray(this.courses), (course: ICurrProgramCourse) => {
      const grades = this.formatMpGrades(course);
      const trend = this.getMpTrend(course.marks);
      const preps = this.getAllPreps(course);
      const warningInfo = this.imStudentCurrentProgram.getCurrSyCourseWarningInfo(this.student, course);
      const subjectArea = ImStudentCurrentProgramHelpers.getSubjectAreaForCourse(course);
      const currMark = ImStudentCurrentProgramHelpers.getCurrentCourseMark(course.marks);
      const currStatus = ImStudentCurrentProgramHelpers.getGradeStatus(currMark);
      const joinedTeacherNames = ImStudentCurrentProgramHelpers.getJoinedTeacherNames(course.teachers);
      const priorityString = ImStudentCurrentProgramHelpers.getPriorityString(course.priority);
      const schedRegentsExamsForSubject = this.imStudentRegents.getScheduledRegentsExamsForSubjectArea(
        this.student,
        course.subjectArea,
        NextRegentsAdminDate,
      );

      const tableRow: ITableRow = {
        schedRegentsExamsForSubject,
        cyclePeriod: course.cyclePeriod,
        period: course.period,
        subjectArea,
        courseCode: course.courseCode + '-' + course.section,
        courseName: course.courseName,
        teacher: joinedTeacherNames,
        creditValue: course.creditValue,
        grades,
        trend,
        status: currStatus,
        priority: priorityString,
        prepsFor: preps,
        courseId: course.courseId,
        warningInfo, // used for warning icon and tooltip
        nx: this.getNxTooltip(course.isCreditsOverride),
      };

      return tableRow;
    });
  }

  // Generates the most recent grade in a particular course
  formatMpGrades({ marks }: ICurrProgramCourse): IMarkWithStatus[] {
    const sorted = ImStudentCurrentProgramHelpers.sortMps(marks);
    const marksWithStatus = sorted.map(mark => {
      const status = ImStudentCurrentProgramHelpers.getGradeStatus(mark);
      const display = ImStudentCurrentProgramHelpers.getHumanReadableCourseMark(mark);
      const markWithStatus = { ...mark, status, display };
      return markWithStatus;
    });
    return marksWithStatus;
  }

  getMpTrend(marks: ICurrProgramCourseMark[], isSorted?: boolean): ICourseMarkTrend {
    const trend: ICourseMarkTrend = {
      previous: null,
      latest: null,
      status: null,
    };

    // If there are not 2 or more marks to compare, do not calc trend
    if (marks.length < 2) return trend;

    // Don't sort twice
    const sorted = isSorted ? marks : ImStudentCurrentProgramHelpers.sortMps(marks);

    const lastIdx = sorted.length - 1;
    const secondToLastIdx = lastIdx > 0 ? lastIdx - 1 : 0;

    trend.previous = sorted[secondToLastIdx].num;
    trend.latest = sorted[lastIdx].num;

    if (trend.previous < trend.latest) trend.status = 'IMPROVING';
    else if (trend.previous > trend.latest) trend.status = 'DECLINING';
    else if (trend.previous === trend.latest) trend.status = 'NO_CHANGE';

    return trend;
  }

  // TODO: Move to IM model? ImStudentCurrProgram
  getAllPreps(courseInfo) {
    const { courseId } = courseInfo;
    const { masterSchedule, userEnteredPrepCourses } = this.currentSchool;
    const coursePrepArray = [];

    _.forEach(masterSchedule, (value: any) => {
      if (value.courseId === courseId) {
        coursePrepArray.push(value.coursePrepsFor);
      }
    });

    _.forEach(userEnteredPrepCourses, (value: any) => {
      if (value.courseId === courseId) {
        coursePrepArray.push(value.coursePrepsFor);
      }
    });

    let filteredCoursePrepArray = _.remove(coursePrepArray, prep => {
      return prep !== null;
    });

    return _.map(filteredCoursePrepArray, course => {
      const regentsSubject = _.find(RegentsSubject, { key: course });

      return regentsSubject ? regentsSubject.humanName : null;
    });
  }

  _updateRegentsSchedCol() {
    _.each(this.tableRows, (row: any) => {
      const subjectArea = row.subjectArea ? row.subjectArea.camelCase : null;

      if (subjectArea) {
        row.schedRegentsExamsForSubject = this.imStudentRegents.getScheduledRegentsExamsForSubjectArea(
          this.student,
          subjectArea,
          NextRegentsAdminDate,
        );
      }
    });
  }

  getNxTooltip(isCreditsOverride: boolean): string {
    if (!isCreditsOverride) return null;
    return `This credit reflects the value of the original course with NX`;
  }
}
